import { CommonModule, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface DvfGaugeComparisonBadge {
  text: string;
  colorClass: string;
  cursorColorClass: string;
}

@Component({
  selector: 'etxe-property-dvf-gauge',
  standalone: true,
  imports: [CommonModule, DecimalPipe, MatIconModule, MatTooltipModule],
  templateUrl: './property-dvf-gauge.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyDvfGaugeComponent {
  currentPriceM2 = input<number | null | undefined>();
  medianPriceM2 = input.required<number>();
  minPriceM2 = input<number | undefined>();
  maxPriceM2 = input<number | undefined>();
  salesCount = input<number | undefined>();
  propertyType = input<string | undefined>();

  effectiveMin = computed<number>(() => {
    const rawMin = this.minPriceM2() ?? Math.round(this.medianPriceM2() * 0.75);
    const current = this.currentPriceM2();
    if (current && current < rawMin) {
      return Math.round(current * 0.95);
    }
    return rawMin;
  });

  effectiveMax = computed<number>(() => {
    const rawMax = this.maxPriceM2() ?? Math.round(this.medianPriceM2() * 1.25);
    const current = this.currentPriceM2();
    if (current && current > rawMax) {
      return Math.round(current * 1.05);
    }
    return rawMax;
  });

  medianPositionPercent = computed<number>(() => {
    const min = this.effectiveMin();
    const max = this.effectiveMax();
    const median = this.medianPriceM2();
    if (max <= min) return 50;
    const pct = ((median - min) / (max - min)) * 100;
    return Math.max(5, Math.min(95, pct));
  });

  currentPositionPercent = computed<number>(() => {
    const current = this.currentPriceM2();
    if (!current) return 50;
    const min = this.effectiveMin();
    const max = this.effectiveMax();
    if (max <= min) return 50;
    const pct = ((current - min) / (max - min)) * 100;
    return Math.max(3, Math.min(97, pct));
  });

  comparisonDiff = computed<number | null>(() => {
    const current = this.currentPriceM2();
    const median = this.medianPriceM2();
    if (!current || !median) return null;
    return Math.round(((current - median) / median) * 100);
  });

  comparisonBadge = computed<DvfGaugeComparisonBadge | null>(() => {
    const diff = this.comparisonDiff();
    if (diff === null) return null;
    const absDiff = Math.abs(diff);

    if (absDiff <= 3) {
      return {
        text: 'Dans le marché (±3 %)',
        colorClass: 'text-jade-11 bg-jade-a3 border-jade-6',
        cursorColorClass: 'bg-emerald-500 ring-emerald-300',
      };
    } else if (diff > 3) {
      return {
        text: `+${absDiff} % au-dessus de la médiane`,
        colorClass:
          diff > 15 ? 'text-ruby-11 bg-ruby-a3 border-ruby-6' : 'text-amber-11 bg-amber-a3 border-amber-6',
        cursorColorClass: diff > 15 ? 'bg-rose-500 ring-rose-300' : 'bg-amber-400 ring-amber-300',
      };
    } else {
      return {
        text: `-${absDiff} % en-dessous de la médiane`,
        colorClass: 'text-teal-11 bg-teal-a3 border-teal-6',
        cursorColorClass: 'bg-teal-500 ring-teal-300',
      };
    }
  });

  detailsTooltip = computed<string>(() => {
    const count = this.salesCount();
    const countStr = count
      ? `basé sur ${count} vente${count > 1 ? 's' : ''} actée${count > 1 ? 's' : ''} dans le secteur`
      : 'données publiques certifiées';
    return `Repère officiel DVF (DGFiP) : ${countStr}. Actes notariés réels récents pour ce type de bien.`;
  });

  cursorTooltip = computed<string>(() => {
    const current = this.currentPriceM2();
    if (!current) return '';
    const badge = this.comparisonBadge();
    return `Position du bien : ${Math.round(current).toLocaleString('fr-FR')} €/m² (${badge?.text ?? ''})`;
  });
}
